import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Truck, Timer, ChevronRight, X, PenLine } from 'lucide-react';
import { portalGet } from '../../lib/portalApi';
import { inrCompact, formatISTDateTime } from '../../lib/format';

const STATUS_COLOR = {
  dispatched: 'bg-amber-100 text-amber-700',
  delivered: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-slate-100 text-slate-500',
};

function PourChip({ pw }) {
  if (!pw) return null;
  if (pw.minutes_used != null) {
    return <span className={`tag ${pw.breached ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>{pw.minutes_used} min after batching{pw.breached ? ' · late' : ''}</span>;
  }
  const cls = pw.state === 'breached' ? 'bg-red-100 text-red-700' : pw.state === 'warning' ? 'bg-amber-100 text-amber-700' : 'bg-teal-50 text-[#1F7A6E]';
  const label = pw.minutes_remaining < 0 ? `${-pw.minutes_remaining} min over pour window` : `${pw.minutes_remaining} min left to pour`;
  return <span className={`tag ${cls}`}>{label}</span>;
}

export default function PortalOrders() {
  const [items, setItems] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    portalGet('/portal/orders').then((d) => setItems(d.items || [])).catch(() => toast.error('Failed to load your orders'));
  }, []);

  if (items === null) return <div className="text-slate-400 py-10 text-center text-sm">Loading…</div>;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-xl font-extrabold text-slate-900">Your Deliveries</h1>
        <p className="text-sm text-slate-500">Every delivery ELR has made to your sites. See "Sales Orders" for what's still pending.</p>
      </div>
      {items.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-sm py-14 text-center text-sm text-slate-400">
          No deliveries yet. Once ELR raises a challan for your order it'll appear here.
        </div>
      )}
      <div className="grid gap-3">
        {items.map((c) => (
          <button key={c.id} onClick={() => setDetail(c)}
            className="text-left bg-white border border-slate-200 rounded-sm p-4 hover:shadow-sm transition-shadow flex items-center justify-between gap-4"
            data-testid={`portal-order-${c.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono text-xs text-[#1F7A6E] font-semibold">{c.challan_no}</span>
                <span className={`tag ${STATUS_COLOR[c.status] || ''}`}>{c.status}</span>
              </div>
              <div className="font-display font-bold">{c.grade} · {c.qty_m3} m³{c.jobsite ? ` — ${c.jobsite}` : ''}</div>
              <div className="text-xs text-slate-500 mt-1 flex items-center gap-3">
                <span className="flex items-center gap-1"><Truck size={11} /> {c.vehicle_no}</span>
                <span className="flex items-center gap-1"><Timer size={11} /> {formatISTDateTime(c.date)}</span>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <PourChip pw={c.pour_window} />
              <div className="text-right">
                <div className="font-display font-bold text-sm">{inrCompact(c.total_amount)}</div>
              </div>
              <ChevronRight size={16} className="text-slate-300" />
            </div>
          </button>
        ))}
      </div>
      {detail && <OrderDetailModal challan={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function OrderDetailModal({ challan, onClose }) {
  return (
    <div className="fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div className="font-display font-bold text-sm">{challan.challan_no}</div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div><div className="text-slate-400 text-xs">Grade</div><span className="font-display font-bold">{challan.grade}</span></div>
            <div><div className="text-slate-400 text-xs">Quantity</div>{challan.qty_m3} m³</div>
            <div><div className="text-slate-400 text-xs">Vehicle</div><span className="font-mono">{challan.vehicle_no}</span></div>
            <div><div className="text-slate-400 text-xs">Status</div><span className={`tag ${STATUS_COLOR[challan.status] || ''}`}>{challan.status}</span> <PourChip pw={challan.pour_window} /></div>
            <div><div className="text-slate-400 text-xs">Dispatched</div>{formatISTDateTime(challan.dispatch_time)}</div>
            {challan.delivered_at && <div><div className="text-slate-400 text-xs">Delivered</div>{formatISTDateTime(challan.delivered_at)}</div>}
          </div>
          {challan.status === 'delivered' && challan.pod_receiver_name && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 space-y-1">
              <div className="kpi-overline flex items-center gap-1"><PenLine size={11} /> Delivery Confirmation</div>
              <div>Received by <b>{challan.pod_receiver_name}</b></div>
              {challan.water_added_l ? <div className="text-xs text-slate-600">Water added at site: {challan.water_added_l} L</div> : null}
              {challan.qty_rejected_m3 ? <div className="text-xs text-red-600">Rejected: {challan.qty_rejected_m3} m³</div> : null}
            </div>
          )}
          {challan.ewb_no && (
            <div className="text-xs text-slate-500">E-way bill: <span className="font-mono">{challan.ewb_no}</span></div>
          )}
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 space-y-1">
            <div className="flex justify-between"><span className="text-slate-500">Amount</span><span>{inrCompact(challan.amount)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">GST ({challan.gst_pct}%)</span><span>{inrCompact(challan.gst_amount)}</span></div>
            <div className="flex justify-between font-display font-bold text-base border-t border-slate-200 pt-1 mt-1"><span>Total</span><span>{inrCompact(challan.total_amount)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
